import {
  type AdminMemberDetail,
  type AdminUserAction,
  asProblem,
  CONSENT_KIND_LABELS,
  formatDate,
  formatPhone,
  HEARD_FROM_LABELS,
  LIVING_SITUATION_LABELS,
  type MemberAddress,
  PROVINCE_LABELS,
  USER_STATUS_LABELS,
} from '@myhome/contracts';
import { Badge } from '@myhome/ui/components/badge';
import { Button } from '@myhome/ui/components/button';
import { ArrowLeft, UserCircle } from '@myhome/ui/icons';
import { cn } from '@myhome/ui/lib/cn';
import { EmptyState } from '@myhome/ui/patterns/empty-state';
import { ListSkeleton } from '@myhome/ui/patterns/list-skeleton';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { MemberBanAction } from '../components/member-ban-action';
import { adminMemberQuery } from '../lib/queries';

export const Route = createFileRoute('/_shell/members/$memberNumber')({
  component: MemberPage,
});

/**
 * One member, as the back office sees them: contact details, every address,
 * how they joined, the full consent record (POPIA) and the account history.
 * Admins can ban the member or lift a ban, with a reason.
 */
function MemberPage() {
  const { memberNumber } = Route.useParams();
  const result = useQuery(adminMemberQuery(memberNumber));

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/members"
        className="inline-flex min-h-(--spacing-touch) items-center gap-1.5 self-start font-medium text-link underline-offset-4 hover:underline"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        All members
      </Link>

      {result.isError ? (
        asProblem(result.error)?.status === 404 ? (
          <EmptyState
            icon={UserCircle}
            title={`No member with number ${memberNumber}`}
            description="Check the number, or search the member list."
          />
        ) : (
          <StatusBanner
            tone="danger"
            title="We could not load this member"
            description="Check your connection and try again."
            action={
              <Button variant="outline" size="sm" onClick={() => result.refetch()}>
                Try again
              </Button>
            }
          />
        )
      ) : result.data === undefined ? (
        <ListSkeleton count={6} shape="row" />
      ) : (
        <MemberDetails member={result.data} />
      )}
    </div>
  );
}

function MemberDetails({ member }: { member: AdminMemberDetail }) {
  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-headline font-semibold text-navy-900">
              {member.firstName} {member.lastName}
            </h1>
            <Badge variant={member.status === 'ACTIVE' ? 'success' : 'danger'}>
              {USER_STATUS_LABELS[member.status]}
            </Badge>
          </div>
          <p className="text-neutral-600 tabular-nums">
            Member {member.memberNumber} · joined{' '}
            {formatDate(new Date(member.memberSince), { year: 'numeric' })} ·{' '}
            {member.lastLoginAt === null
              ? 'never signed in'
              : `last signed in ${formatDate(new Date(member.lastLoginAt), {
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
          </p>
        </div>
        <MemberBanAction member={member} />
      </header>

      {member.status === 'SUSPENDED' ? (
        <StatusBanner
          tone="danger"
          title="This member is banned and cannot sign in"
          description={member.banReason === null ? undefined : `Reason: ${member.banReason}`}
        />
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Contact">
          <Row label="Email">
            <span className="break-all">{member.email}</span>
            <Badge variant={member.emailVerified ? 'success' : 'warning'} className="mt-1 self-start">
              {member.emailVerified ? 'Verified' : 'Not verified'}
            </Badge>
          </Row>
          <Row label="Mobile">
            <a href={`tel:${member.phone}`} className="tabular-nums text-link underline underline-offset-4">
              {formatPhone(member.phone)}
            </a>
          </Row>
          <Row label="Home">{LIVING_SITUATION_LABELS[member.livingSituation]}</Row>
        </Card>

        <Card title="How they joined">
          <Row label="Heard about us from">{HEARD_FROM_LABELS[member.heardFrom]}</Row>
          <Row label="Agent referral code">
            {member.referralCode === null ? (
              <span className="text-neutral-600">None</span>
            ) : (
              <span className="font-medium tabular-nums">{member.referralCode}</span>
            )}
          </Row>
          <Row label="Member since">
            <span className="tabular-nums">
              {formatDate(new Date(member.memberSince), {
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </Row>
        </Card>
      </div>

      <section aria-labelledby="member-addresses" className="flex flex-col gap-2">
        <h2 id="member-addresses" className="font-heading text-title font-semibold text-navy-900">
          Addresses <span className="text-neutral-600 tabular-nums">({member.addresses.length})</span>
        </h2>
        <ul className="grid gap-3 md:grid-cols-2">
          {member.addresses.map((address) => (
            <li
              key={address.id}
              className="rounded-(--radius-card) border border-border bg-surface px-4 py-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-navy-900">{address.label}</h3>
                {address.isPrimary ? <Badge variant="neutral">Main address</Badge> : null}
                {address.areaId === null ? <Badge variant="info">Suburb not listed</Badge> : null}
              </div>
              <address className="mt-1 text-navy-900 not-italic">
                {addressLines(address).map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </address>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="member-history" className="flex flex-col gap-2">
        <div>
          <h2 id="member-history" className="font-heading text-title font-semibold text-navy-900">
            Account history
          </h2>
          <p className="text-neutral-600">Bans and other admin actions on this account, newest first.</p>
        </div>
        {member.history.length === 0 ? (
          <p className="rounded-(--radius-card) border border-dashed border-border-strong bg-surface px-4 py-4 text-neutral-600">
            No admin actions on this account.
          </p>
        ) : (
          <ol className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
            {member.history.map((entry) => (
              <li
                key={`${entry.action}-${entry.at}`}
                className="flex flex-col gap-0.5 border-t border-border px-4 py-2.5 first:border-t-0"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={HISTORY[entry.action].tone}>{HISTORY[entry.action].label}</Badge>
                  <span className="text-eyebrow text-neutral-600 tabular-nums">
                    {formatDate(new Date(entry.at), { year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    {' · '}
                    {entry.actorName ?? 'A removed admin'}
                  </span>
                </div>
                {entry.reason === null ? null : <p className="text-navy-900">{entry.reason}</p>}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section aria-labelledby="member-consents" className="flex flex-col gap-2">
        <div>
          <h2 id="member-consents" className="font-heading text-title font-semibold text-navy-900">
            Consent record
          </h2>
          <p className="text-neutral-600">Every choice the member has made, newest first.</p>
        </div>
        <div className="overflow-x-auto rounded-(--radius-card) border border-border bg-surface">
          <table className="w-full border-collapse text-left">
            <thead className="border-b border-border bg-navy-50">
              <tr>
                {['Consent', 'Choice', 'Version', 'When'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-3 py-2 text-eyebrow font-semibold text-neutral-700 uppercase tracking-[0.04em]"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {member.consents.map((consent) => (
                <tr key={`${consent.kind}-${consent.at}`} className="border-t border-border first:border-t-0">
                  <td className="px-3 py-2">{CONSENT_KIND_LABELS[consent.kind]}</td>
                  <td className="px-3 py-2">
                    {consent.granted ? (
                      <Badge variant="success">
                        {consent.kind === 'MARKETING' ? 'Opted in' : 'Accepted'}
                      </Badge>
                    ) : (
                      <Badge variant="neutral">
                        {consent.kind === 'MARKETING' ? 'Opted out' : 'Declined'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">{consent.version}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                    {formatDate(new Date(consent.at), {
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

/** How each audit action reads in the account history. */
const HISTORY: Record<AdminUserAction, { label: string; tone: 'danger' | 'success' | 'neutral' }> = {
  'user.banned': { label: 'Banned', tone: 'danger' },
  'user.unbanned': { label: 'Ban lifted', tone: 'success' },
  'user.sessions_revoked': { label: 'Signed out everywhere', tone: 'neutral' },
};

/** Street, suburb and city, then province and postal code, skipping what is missing. */
function addressLines(address: MemberAddress): string[] {
  const city = address.city === address.suburb ? null : address.city;
  const place = [address.suburb, city].filter((part): part is string => part !== null).join(', ');
  return [
    address.line1,
    address.line2,
    place,
    [PROVINCE_LABELS[address.province], address.postalCode].filter(Boolean).join(' '),
  ].filter((line): line is string => line !== null && line !== '');
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  const id = `member-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h2 id={id} className="font-heading text-title font-semibold text-navy-900">
        {title}
      </h2>
      <dl className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">{children}</dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-0.5 border-t border-border px-4 py-2.5 first:border-t-0')}>
      <dt className="text-eyebrow text-neutral-600">{label}</dt>
      <dd className="flex flex-col font-medium text-navy-900">{children}</dd>
    </div>
  );
}
